import { Ticket, Calendar } from "lucide-react";
import type { ChequesData, ChequeItem } from "@/features/subventions/types/subsidy-sumup.types";
import { eur } from "@/features/subventions/utils/subventionStatus";

type ChequeCardProps = { cheque: ChequeItem };

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const ChequeCard = ({ cheque }: ChequeCardProps) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <div className="flex items-start justify-between gap-2.5">
      <span className="text-sm font-bold text-gray-900 dark:text-white">
        {cheque.motif || "Chèque cadeau"}
      </span>
      <span className="whitespace-nowrap text-base font-extrabold text-primary-emphasis">
        {eur(cheque.available_amount)}
      </span>
    </div>

    {cheque.expires_at && (
      <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-gray-400 dark:text-gray-500">
        <Calendar size={14} aria-hidden />
        <span>
          Valable jusqu'au
          {" "}
          {formatDate(cheque.expires_at)}
        </span>
      </div>
    )}

    {cheque.eligible_categories.length > 0 && (
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {cheque.eligible_categories.map((category) => (
          <span
            key={category}
            className="rounded-full bg-info/[0.14] px-2.5 py-0.5 text-[11px] font-semibold text-info-emphasis"
          >
            {category}
          </span>
        ))}
      </div>
    )}
  </div>
);

type ChequesFeedCardProps = { cheques: ChequesData };

// Periwinkle in-feed card matching the urgency-first redesign. Used by SubventionsFeedView.
export const ChequesFeedCard = ({ cheques }: ChequesFeedCardProps) => {
  if (!cheques.enabled || cheques.cheques.length === 0) {
    return null;
  }

  const count = cheques.cheques.length;
  const plural = count > 1 ? "s" : "";

  return (
    <div className="flex flex-col gap-3.5 rounded-2xl border border-primary/15 bg-lightprimary p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Ticket size={20} className="text-primary" aria-hidden />
        </div>
        <div className="flex flex-col">
          <h2 className="text-base font-bold text-foreground">Mes chèques cadeaux</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {count}
            {" chèque"}
            {plural}
            {" disponible"}
            {plural}
          </span>
        </div>
        <span className="ml-auto text-lg font-extrabold text-primary-emphasis">
          {eur(cheques.total_available)}
        </span>
      </div>

      <div className="columns-1 gap-3 xl:columns-2 [&>*]:mb-3 [&>*]:break-inside-avoid-column">
        {cheques.cheques.map((cheque) => (
          <ChequeCard key={cheque.id} cheque={cheque} />
        ))}
      </div>
    </div>
  );
};

ChequesFeedCard.displayName = "ChequesFeedCard";
